import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, User, Mail, Shield, UserCheck, Save, 
  Loader2, LogOut, Lock, Eye, EyeOff, CreditCard, Zap
} from "lucide-react";
import BillingSection from "@/components/BillingSection";

import { getApiBaseUrl } from '@/config/api';

const API = getApiBaseUrl();

export default function Account() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateUser, logout, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || ""
  });

  // Get active tab from URL or default to "profile"
  const activeTab = searchParams.get("tab") || "profile";
  
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await updateUser({ name: formData.name });
      if (result.success) {
        toast.success("Profile updated!");
      } else {
        toast.error(result.error || "Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Logged out successfully");
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Password changed successfully!");
      setShowPasswordForm(false);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to change password";
      toast.error(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: "bg-red-500/20 text-red-400 border-red-500/30",
      director: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
      actor: "bg-amber-500/20 text-amber-400 border-amber-500/30"
    };
    const labels = {
      super_admin: "Super Admin",
      director: "Director",
      actor: "Actor"
    };
    return (
      <Badge variant="outline" className={styles[role] || styles.director}>
        {labels[role] || role}
      </Badge>
    );
  };

  const getRoleIcon = (role) => {
    if (role === "super_admin") return <Shield className="w-6 h-6 text-red-400" />;
    if (role === "director") return <UserCheck className="w-6 h-6 text-indigo-400" />;
    return <User className="w-6 h-6 text-amber-400" />;
  };

  return (
    <div className="min-h-screen bg-[#09090b] p-4 md:p-8 animate-fadeIn">
      {/* Header */}
      <header className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-zinc-400 hover:text-white"
            data-testid="account-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Account Settings</h1>
            <p className="text-sm text-zinc-500">Manage your profile and subscription</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-900 border-zinc-800 mb-6">
            <TabsTrigger 
              value="profile" 
              className="data-[state=active]:bg-zinc-800"
              data-testid="profile-tab"
            >
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value="billing" 
              className="data-[state=active]:bg-zinc-800"
              data-testid="billing-tab"
            >
              <Zap className="w-4 h-4 mr-2" />
              Billing & Sparks
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            {/* Profile Card */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center">
                    {getRoleIcon(user?.role)}
                  </div>
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      {user?.name || user?.email}
                      {getRoleBadge(user?.role)}
                    </CardTitle>
                    <CardDescription className="text-zinc-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {user?.email}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-zinc-300">Display Name</Label>
                    <Input
                      id="name"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      data-testid="account-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Email</Label>
                    <Input
                      value={user?.email}
                      disabled
                      className="bg-zinc-800 border-zinc-700 text-zinc-500"
                    />
                    <p className="text-xs text-zinc-500">Email cannot be changed</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Role</Label>
                    <Input
                      value={user?.role === "super_admin" ? "Super Admin" : user?.role === "director" ? "Director" : "Actor"}
                      disabled
                      className="bg-zinc-800 border-zinc-700 text-zinc-500"
                    />
                    <p className="text-xs text-zinc-500">Role is assigned by administrators</p>
                  </div>

                  <Button 
                    type="submit" 
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    disabled={loading}
                    data-testid="account-save-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Password Change Card */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Security
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Manage your account password
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!showPasswordForm ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPasswordForm(true)}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    data-testid="change-password-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                ) : (
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword" className="text-zinc-300">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="currentPassword"
                          type={showCurrentPassword ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white pr-10"
                          required
                          data-testid="current-password-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-zinc-400 hover:text-white"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-zinc-300">New Password</Label>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white pr-10"
                          required
                          minLength={8}
                          data-testid="new-password-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-zinc-400 hover:text-white"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-zinc-500">Minimum 8 characters</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-zinc-300">Confirm New Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        required
                        data-testid="confirm-password-input"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        type="submit" 
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        disabled={passwordLoading}
                        data-testid="submit-password-change-btn"
                      >
                        {passwordLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Lock className="w-4 h-4 mr-2" />
                        )}
                        Update Password
                      </Button>
                      <Button 
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
                        }}
                        className="text-zinc-400"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Session</CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  variant="outline" 
                  onClick={handleLogout}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <BillingSection />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
